import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { ValidatorForm } from 'react-material-ui-form-validator';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Box, TextField, Typography, FormControl, Select, MenuItem, Checkbox, ListItemIcon } from '@mui/material';
import EventBus from 'eventing-bus';

import constants from '../../constants';
import { getPipelines } from '../../store/Deals';
import { createObject } from '../../store/Objects';
import { getSearchResults } from '../../store/Auth';
import PropertyField from '../../components/Properties/PropertyField';
import ViewStateManager from '../../utils/viewStateManager';

const AddObjectModal = ({ objectType, isOpen, onClose, onSuccess }) => {

  const dispatch = useDispatch();
  const { pipelines } = useSelector(state => state['Deals']);
  const { searchResponse } = useSelector(state => state['Auth']);
  const { currentWorkspace } = useSelector(state => state['Settings']);

  const objectSchema = constants.getObjectSchema(objectType);
  const { properties } = useSelector(state => state['Objects'][objectSchema['type']]) || {};

  const isDeal = objectSchema['type'] === 'deals';
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchText, setSearchText] = useState({ contact: '', company: '' });
  const [workspaceUsers, setWorkspaceUsers] = useState([]);

  // Initialize form data based on properties
  useEffect(() => {
    if (isDeal) {
      // For deals, start with the deal form structure and add custom fields
      const initialData = { ...(constants['newDealForm'] || {}), customFields: {} };
      
      // Add any additional properties with showInForm
      if (properties) {
        Object.values(properties).forEach(group => {
          if (Array.isArray(group)) {
            group.forEach(prop => {
              if (prop['displayConfig']?.['showInForm'] && prop['name']) {
                const defaultValue = prop['defaultValue'] || '';
                // Skip fields already in newDealForm
                if (!(prop['name'] in initialData)) {
                  // Check if it's a custom field (isSystem: false)
                  if (prop['isSystem'] === false) {
                    initialData.customFields[prop['name']] = defaultValue;
                  } else {
                    initialData[prop['name']] = defaultValue;
                  }
                }
              }
            });
          }
        });
      }
      setFormData(initialData);
    } else {
      // For other objects, use property-based initialization
      const initialData = { customFields: {} };
      if (properties) {
        Object.values(properties).forEach(group => {
          if (Array.isArray(group))
            group.forEach(prop => {
              if (prop['displayConfig']?.['showInForm'] && prop['name']) {
                const defaultValue = prop['defaultValue'] || '';
                // Check if it's a custom field (isSystem: false)
                if (prop['isSystem'] === false) {
                  initialData.customFields[prop['name']] = defaultValue;
                } else {
                  initialData[prop['name']] = defaultValue;
                }
              }
            })
        })
      }
      setFormData(initialData);
    }
  }, [properties, objectType, isDeal]);

  // Fetch pipelines and search results for deals
  useEffect(() => {
    if (isOpen && isDeal) {
      dispatch(getPipelines());
      dispatch(getSearchResults({ search: '', objects: Object.values(constants['objectsSchema']).map(schema => schema['plural']) }));

      // Get workspace users for Deal Owner dropdown
      if (currentWorkspace?.['members']) {
        setWorkspaceUsers(currentWorkspace['members']);
      }
    }
  }, [isOpen, isDeal, currentWorkspace]);

  // Auto-set pipeline from saved state or first available
  useEffect(() => {
    if (isDeal && pipelines.length > 0 && !formData.pipelineId) {
      // Try to get saved pipeline from ViewStateManager
      const savedPipelineId = ViewStateManager.getSelectedPipeline();
      const savedPipeline = savedPipelineId ? pipelines.find(p => p['_id'] === savedPipelineId) : null;

      // Use saved pipeline if it exists, otherwise use first pipeline
      const pipelineToUse = savedPipeline || pipelines[0];
      const firstStage = pipelineToUse['stages']?.[0];

      setFormData(prevData => ({
        ...prevData,
        pipeline: pipelineToUse,
        pipelineId: pipelineToUse['_id'],
        stage: firstStage?.['id'] || '',
        // Set default ownerId to current user if not already set
        ownerId: prevData.ownerId || currentWorkspace?.['userId'] || ''
      }));
    }
  }, [pipelines, isDeal, currentWorkspace])

  const handleSubmit = () => {
    // Special validation for contacts - require at least one of firstName, lastName, or email
    if (objectType === 'contacts') {
      const firstName = formData['firstName']?.trim();
      const lastName = formData['lastName']?.trim();
      const email = formData['email']?.trim();

      if (!firstName && !lastName && !email) {
        EventBus.publish('error', 'At least one of First Name, Last Name, or Email is required');
        return;
      }
    } else {
      // Validate required fields based on properties for non-contact objects
      if (properties) {
        const errors = [];
        Object.values(properties).forEach(group => {
          if (Array.isArray(group)) {
            group.forEach(prop => {
              if (prop['displayConfig']?.['showInForm'] && prop['validation']?.['required']) {
                const value = prop['isSystem'] === false
                  ? formData.customFields?.[prop['name']]
                  : formData[prop['name']];

                if (!value || (typeof value === 'string' && !value.trim())) {
                  errors.push(`${prop['label']} is required`);
                }
              }
            });
          }
        });

        if (errors.length > 0) {
          EventBus.publish('error', errors[0]); // Show first error
          return;
        }
      }
    }

    // Additional validation for deal-specific fields
    if (isDeal) {
      if (!formData['pipelineId']) {
        EventBus.publish('error', 'Please select a pipeline');
        return;
      }
      if (!formData['stage']) {
        EventBus.publish('error', 'Please select a deal stage');
        return;
      }
    }

    setIsSubmitting(true);
    dispatch(createObject({
      data: formData,
      objectType: objectSchema['type'],
      failCallback: () => setIsSubmitting(false),
      successCallback: (newObject) => { 
        onSuccess(newObject); 
        setIsSubmitting(false);
        if (isDeal) setFormData(constants['newDealForm'] || {});
      }
    }))
  }

  const handleFieldChange = (fieldName, value) => {
    // Find the property configuration to check if it's a custom field
    let isCustomField = false;
    if (properties) {
      Object.values(properties).forEach(group => {
        if (Array.isArray(group)) {
          const prop = group.find(p => p['name'] === fieldName);
          if (prop && prop['isSystem'] === false) {
            isCustomField = true;
          }
        }
      });
    }

    if (isCustomField) {
      // Update custom field within customFields object
      setFormData({
        ...formData,
        customFields: {
          ...formData.customFields,
          [fieldName]: value
        }
      });
    } else {
      // Update system field at root level
      setFormData({ ...formData, [fieldName]: value });
    }
  };
  const handleFormChange = (event) => setFormData({ ...formData, [event['target']['name']]: event['target']['value'] });

  const handleContactClick = (id) => {
    let contacts = [...(formData['contacts'] || [])];
    if (contacts.includes(id))
      contacts = contacts.filter(contactId => contactId !== id);
    else contacts.push(id);
    setFormData({ ...formData, ['contacts']: contacts });
  }

  const handleCompanyClick = (id) => {
    let companies = [...(formData['companies'] || [])];
    if (companies.includes(id))
      companies = companies.filter(companyId => companyId !== id);
    else companies.push(id);
    setFormData({ ...formData, ['companies']: companies });
  }

  // Reusable function to collect and sort form properties
  const getFormProperties = (excludeFields = []) => {
    console.log('********* properties', properties);
    const formProperties = [];
    if (properties) {
      Object.values(properties).forEach(group => {
        if (Array.isArray(group)) {
          group.forEach(prop => {
            if (prop['displayConfig']?.['showInForm'] && !prop['displayConfig']?.['readOnly']) {
              // Skip excluded fields and ownerId (handled separately)
              if (!excludeFields.includes(prop['name']) && prop['name'] !== 'ownerId') {
                formProperties.push(prop);
              }
            }
          });
        }
      });
    }

    // Sort by order field and group
    formProperties.sort((a, b) => {
      const groupOrder = { 'Basic Information': 1, 'Professional Information': 2, 'Contact Information': 3, 'Default': 999 };
      const aGroupOrder = groupOrder[a['group']] || groupOrder['Default'];
      const bGroupOrder = groupOrder[b['group']] || groupOrder['Default'];
      if (aGroupOrder !== bGroupOrder) return aGroupOrder - bGroupOrder;
      return (a['order'] || 1000) - (b['order'] || 1000);
    });

    return formProperties;
  }

  // Reusable function to render a property field
  const renderPropertyField = (prop) => {
    const validators = [], errorMessages = [];

    // For contacts, don't make firstName/lastName individually required since we have custom validation
    if (prop['validation']?.['required'] && !(objectType === 'contacts' && (prop['name'] === 'firstName' || prop['name'] === 'lastName'))) {
      validators.push('required');
      errorMessages.push(`${prop['label']} is required`);
    }

    if (prop['type'] === 'email') {
      validators.push('isEmail');
      errorMessages.push('Enter valid email');
    }

    return (
      <div key={prop['name']} className='group-form'>
        <PropertyField
          mode='edit'
          property={prop}
          library='reactstrap'
          validators={validators}
          onChange={handleFieldChange}
          errorMessages={errorMessages}
          value={prop['isSystem'] === false ? formData.customFields?.[prop['name']] : formData[prop['name']]}
          additionalData={{ users: currentWorkspace?.['members'] || [] }}
        />
      </div>
    );
  }

  const renderDealFields = () => {
    // Get all form properties, excluding pipeline and stage which need special handling
    const formProperties = getFormProperties(['title', 'pipelineId', 'stage']);
    return (
      <>
        {/* Render pipeline first as it affects stage options */}
        {/* Show deal name here */}
        <div className='group-form'>
          <label>Deal Title *</label>
          <TextField
            fullWidth
            variant='standard'
            value={formData['title'] || ''}
            onChange={(e) => setFormData({ ...formData, ['title']: e['target']['value'] })}
          />
        </div>
        <div className='group-form'>
          <label>Pipeline *</label>
          <FormControl>
            <Select
              displayEmpty
              value={formData['pipelineId'] || ''}
              renderValue={(selected) => {
                if (!selected) return 'Select Pipeline';
                const selectedOption = pipelines.find((option) => option['_id'] === selected);
                return selectedOption ?
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '14px' }}>{selectedOption['name']}</Typography>
                  </Box>
                : null;
              }}
              MenuProps={{ disableAutoFocusItem: true }}
            >
              {pipelines.length > 0 ? pipelines.map((pipeline) => (
                <MenuItem
                  key={pipeline?.['_id']}
                  value={pipeline?.['_id']}
                  onClick={() => {
                    const firstStage = pipeline?.stages?.[0];
                    setFormData({ 
                      ...formData, 
                      'pipelineId': pipeline?.['_id'], 
                      pipeline,
                      'stage': firstStage?.id || ''
                    });
                  }}
                >
                  {pipeline?.['name']}
                </MenuItem>
              )) : <MenuItem disabled>No pipelines found</MenuItem>}
            </Select>
          </FormControl>
        </div>

        {/* Render stage after pipeline */}
        <div className='group-form'>
          <label>Deal stage *</label>
          <FormControl>
            <Select
              displayEmpty
              value={formData['stage'] || ''}
              onChange={(e) => {
                e.stopPropagation();
                setFormData({ ...formData, stage: e.target.value });
              }}
              renderValue={(selected) => {
                if (!selected || selected == undefined) return 'Select Stage';
                const stage = formData['pipeline']?.['stages']?.find((stage) => stage['id'] === selected);
                return stage ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '14px' }}>{stage['name']}</Typography>
                  </Box>
                ) : 'Select Stage';
              }}
              MenuProps={{ disableAutoFocusItem: true }}
            >
              {formData['pipeline'] && formData['pipeline']?.['stages']?.length > 0 ? formData['pipeline']['stages'].map((stage) => (
                <MenuItem
                  key={stage['id']}
                  value={stage['id']}
                >
                  {stage['name']}
                </MenuItem>
              )) : <MenuItem disabled>Please select a pipeline</MenuItem>}
            </Select>
          </FormControl>
        </div>

        {/* Render Deal Owner dropdown */}
        <div className='group-form'>
          <label>Deal Owner</label>
          <FormControl>
            <Select
              displayEmpty
              value={formData['ownerId'] || ''}
              onChange={(e) => {
                e.stopPropagation();
                setFormData({ ...formData, ownerId: e.target.value });
              }}
              renderValue={(selected) => {
                if (!selected) return 'None';
                const selectedUser = workspaceUsers.find((user) => user['userId'] === selected);
                return selectedUser ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '14px' }}>
                      {selectedUser['firstName'] || ''} {selectedUser['lastName'] || ''}
                    </Typography>
                  </Box>
                ) : 'None';
              }}
              MenuProps={{ disableAutoFocusItem: true }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {workspaceUsers.length > 0 ? workspaceUsers.map((user) => (
                <MenuItem
                  key={user['userId']}
                  value={user['userId']}
                >
                  {user['firstName'] || ''} {user['lastName'] || ''}
                </MenuItem>
              )) : <MenuItem disabled>No users found</MenuItem>}
            </Select>
          </FormControl>
        </div>

        {/* Dynamically render all other properties using reusable function */}
        {formProperties.map(renderPropertyField)}

        <hr />
        <h4>Associate Deal with</h4>
        {renderAssociationFields()}
      </>
    )
  }

  const renderAssociationFields = () => {
    return (
      <>
        <div className='group-form'>
          <label>Contact</label>
          <FormControl>
            <Select
              multiple
              displayEmpty
              label='Contact'
              value={formData['contacts'] || []}
              renderValue={(selected) => {
                if (!selected || selected.length == 0) return 'Select Contact';
                return formData['contacts'].map(contact => {
                  const contactObj = searchResponse['results']?.['Contacts']?.find(obj => obj['_id'] == contact);
                  return contactObj ? `${contactObj['firstName'] || ''} ${contactObj['lastName'] || ''} ${contactObj['email'] ? `(${contactObj['email']})` : ''}` : '';
                }).filter(name => name).join(', ');
              }}
            >
              <MenuItem disableRipple>
                <TextField
                  fullWidth
                  autoFocus
                  variant='standard'
                  value={searchText.contact || ''}
                  placeholder='Search...'  
                  onKeyDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setSearchText({ ...searchText, contact: e['target']['value'] })}
                />
              </MenuItem>
              {searchResponse['results']?.['Contacts'] && searchResponse['results']?.['Contacts']?.filter(obj => {
                const searchLower = searchText.contact?.toLowerCase() || '';
                return `${obj['firstName']} ${obj['lastName']} ${obj['email']}`.toLowerCase().includes(searchLower);
              }).map(obj => {
                  return (
                    <MenuItem
                      key={obj['_id']}
                      value={obj['_id']}
                      onClick={() => handleContactClick(obj['_id'])}>
                      <ListItemIcon>
                        <Checkbox
                          checkedIcon={<CheckBoxIcon />}
                          icon={<CheckBoxOutlineBlankIcon />}
                          checked={formData['contacts']?.includes(obj['_id'])}
                        />
                      </ListItemIcon>
                      {`${obj['firstName'] || ''} ${obj['lastName'] || ''} ${obj['email'] && `(${obj['email']})`}`}
                    </MenuItem>
                  )
                })
              }
            </Select>
          </FormControl>
        </div>
        
        <div className='group-form'>
          <label>Company</label>
          <FormControl>
            <Select
              multiple
              displayEmpty
              label='Company'
              value={formData['companies'] || []}
              renderValue={(selected) => {
                if (!selected || selected.length == 0) return 'Select Company';
                return formData['companies'].map(company => {
                  const companyObj = searchResponse['results']?.['Companies']?.find(obj => obj['_id'] == company);
                  return companyObj ? companyObj['name'] : '';
                }).filter(name => name).join(', ');
              }}
            >
              <MenuItem disableRipple>
                <TextField
                  fullWidth
                  autoFocus
                  variant='standard'
                  value={searchText.company || ''}
                  placeholder='Search...'  
                  onKeyDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setSearchText({ ...searchText, company: e['target']['value'] })}
                />
              </MenuItem>
              {searchResponse['results']?.['Companies'] && searchResponse['results']?.['Companies']?.filter(obj => {
                const searchLower = searchText.company?.toLowerCase() || '';
                return obj['name']?.toLowerCase().includes(searchLower);
              }).map(obj => {
                  return (
                    <MenuItem
                      key={obj['_id']}
                      value={obj['_id']}
                      onClick={() => handleCompanyClick(obj['_id'])}>
                      <ListItemIcon>
                        <Checkbox
                          checkedIcon={<CheckBoxIcon />}
                          icon={<CheckBoxOutlineBlankIcon />}
                          checked={formData['companies']?.includes(obj['_id'])}
                        />
                      </ListItemIcon>
                      {obj['name']}
                    </MenuItem>
                  )
                })
              }
            </Select>
          </FormControl>
        </div>
      </>
    )
  }

  const renderFormFields = () => {
    if (isDeal) return renderDealFields();
    if (!properties) return null;

    // Get all form properties (including ownerId for non-deals)
    const formProperties = getFormProperties();
    
    // Add ownerId for non-deal objects if it exists in properties
    const ownerProperty = Object.values(properties).flat().find(prop => prop['name'] === 'ownerId');
    if (ownerProperty) {
      formProperties.push(ownerProperty);
    }

    return formProperties.map(renderPropertyField);
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} backdrop="static" keyboard={true} modalClassName='right right-without-bg' className={`main-modal right right-side-modal style-two`}>
      <ModalHeader toggle={onClose}><h3>{isDeal ? 'Create Deal' : `Add New ${objectSchema?.['label'] || objectType}`}</h3></ModalHeader>
      <ModalBody>
        <div className='filters-modal-content'>
          <div className='createdeal-form'>
            <ValidatorForm onSubmit={handleSubmit} autoComplete='off'>
              <div className='form-area'>
                {renderFormFields()}
              </div>
              <div className='group-form btn-groups'>
                <button type='button' className='btn-style-border' onClick={onClose}>
                  Cancel
                </button>
                <button type='submit' className='btn-style-new' disabled={isSubmitting}>
                  {isSubmitting ? <><i className="fa fa-spinner fa-spin"></i> Creating...</> : 'Create'}
                </button>
              </div>
            </ValidatorForm>
          </div>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default AddObjectModal